/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
import CreateIcon from "@mui/icons-material/Add";
import EditIcon from "@Assets/icons/edit.svg?react";
import DeleteIcon from "@Assets/icons/svg/delete.svg?react";
import CancelIcon from "@Assets/icons/svg/Cancel.svg?react";
import SlickGrid1, { type ColumnDefinition, type ValidatedColumnDefinition } from "@CubeComponents/html/SlickGrid1";
import { useEffect, useMemo, useRef, useState } from "react";
import { FormControl, Select } from "@mui/material";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import { v4 as uuidv4 } from "uuid";
import UpdateIcon from "@Assets/icons/svg/Update.svg?react";
import { TOAST_TYPE_ERROR } from "@Services/constants";
import { showToastNotification } from "@Reducers/toastSlice";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { useTranslation } from "@i18n";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import DownArrowIcon from "@Assets/icons/svg/arrowDown.svg?react";
import {
  EditorTemplateArgs,
  ToolbarItem,
  ResourceAddEditData,
  ResourceInGanttProps,
  ResourceItem,
  DeletedResource,
} from "@WebTypes/scheduleDomain";
import MenuItemComponent from "@CubeComponents/html/MenuItemComponent";
import { resolveTaskResources } from "./useGanttHelper";
import type { ScheduleGridRef } from "../interface";
import type { GanttStatic } from "./dhtmlxgantt";

declare const gantt: GanttStatic;

/**
 * A row of the resource grid, and the edit buffer behind it. Rows reach the grid from three places
 * with slightly different shapes — persisted assignments (`ResourceAddEditData`), assignments
 * resolved off the Gantt task (`ResourceAssignment`) and the blank row `addResourceIds` seeds — so
 * every cell is optional and the numeric cells may still hold the empty string the blank row starts
 * with. `cost` exists only on the grid, never on `ResourceAddEditData`.
 */
type ResourceGridRow = {
  id?: string | number;
  resource_id?: string | number;
  counter?: string | number;
  text?: string;
  availability?: string | number;
  cost?: string | number;
  type?: string;
  selectionSetIds?: string[];
};

function ResourceInGantt(props: ResourceInGanttProps) {
  const { t } = useTranslation();
  const { deletedResources, setDeletedResources, resourceAddEditData, setResourceAddEditData } = props;
  const [resourceDataset, setResourceDataset] = useState<ResourceGridRow[]>([]);
  const [disableEditInResource, setDisableEditInResource] = useState(true);
  const [disableDeleteInResource, setDisableDeleteInResource] = useState(true);
  const [disableAssignInResource, setDisableAssignInResource] = useState(false);
  const [editClickedInResource, setEditClickedInResource] = useState(false);
  const [resourceEditItemsData, setResourceEditItemsData] = useState<ResourceGridRow>({});
  const [resourceEditMode, setResourceEditMode] = useState(false);
  const [firstRowSelected, setFirstRowSelected] = useState(false);
  const slickGridRef = useRef<ScheduleGridRef<ResourceGridRow> | null>(null);
  const dispatch = useDispatch();

  const formControlSX = {
    "& .MuiInputBase-root": {
      height: "2rem",
      backgroundColor: "white",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#666A84",
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#6BBBE3",
      borderWidth: "0.0625rem",
    },
  };

  const wideFormControlSX = {
    ...formControlSX,
    "& .MuiInputBase-root": {
      height: "2rem",
      backgroundColor: "white",
    },
  };

  const selectMenuProps = {
    PaperProps: {
      sx: {
        maxHeight: 260,
        overflow: "auto",
        maxWidth: "50%",
      },
    },
    MenuListProps: {
      sx: {
        py: 0,
      },
    },
  };

  const selectComponentSX = {
    "& .MuiInputBase-root": {
      height: "2rem",
      py: 0,
    },
    "& .MuiSelect-icon": {
      top: "50%",
      transform: "translateY(-50%)",
      width: "1rem",
      height: "1rem",
    },
  };

  useEffect(() => {
    const selectedId = gantt.getSelectedId();
    if (selectedId) {
      const selectedTask = gantt.getTask(selectedId);
      const taskResources = resolveTaskResources(selectedTask);
      const editedResources = resourceAddEditData || [];
      const resourceRows = [...editedResources, ...taskResources] as ResourceGridRow[];
      resourceRows.forEach((resource) => {
        if (!resource.id) {
          resource.id = uuidv4();
        }
      });
      setResourceDataset(resourceRows);
    }
  }, []);

  const slickGridOptions = useMemo(
    () => ({
      forceFitColumns: true,
      enableGridMenu: false,
      enableFiltering: false,
      enableCellMenu: false,
      enableHeaderMenu: false,
    }),
    [],
  );

  const handleResourceFields = (field: keyof ResourceItem, value: string | number) => {
    const tempFields = { ...resourceEditItemsData, [field]: value };
    if (field === "counter" || field === "text") {
      const matchedResource = gantt
        .serverList("resourceOptions")
        .find((resource: ResourceItem) => resource[field] === value);
      if (matchedResource) {
        Object.assign(tempFields, {
          text: matchedResource.text,
          counter: matchedResource.counter,
          resource_id: matchedResource.id,
          type: matchedResource.type,
          selectionSetIds: matchedResource.selectionSetIds,
          queriesIds: matchedResource.queriesIds,
        });
      }
    }
    setResourceEditItemsData(tempFields);
  };
  const resourceIdEditorTemplate = (args: EditorTemplateArgs<ResourceGridRow>) => {
    const allResources = gantt.serverList("resourceOptions");
    // The editor templates only run while the grid is mounted, so the handle is set. Erased
    // assertion; a guard here would change behaviour.
    const resourceItems = slickGridRef.current!.dataView.items;
    const dropdownItems = allResources.filter(
      (resource: ResourceItem) =>
        resource.counter === args.args.counter ||
        !resourceItems.some((datasetItem: ResourceGridRow) => datasetItem.counter === resource.counter),
    );
    return (
      <div className="w-full">
        <FormControl fullWidth sx={formControlSX}>
          <Select
            IconComponent={DownArrowIcon}
            onChange={(e) => handleResourceFields("counter", e.target.value)}
            value={resourceEditItemsData.counter}
            disabled={editClickedInResource}
            MenuProps={selectMenuProps}
            sx={selectComponentSX}
          >
            {dropdownItems.map((item: ResourceItem) => {
              return (
                <MenuItemComponent key={item.counter} value={item.counter}>
                  {item.counter}
                </MenuItemComponent>
              );
            })}
          </Select>
        </FormControl>
      </div>
    );
  };
  const resourceNameEditorTemplate = (args: EditorTemplateArgs<ResourceGridRow>) => {
    const allResources = gantt.serverList("resourceOptions");
    const resourceItems = slickGridRef.current!.dataView.items;
    const dropdownItems = allResources.filter(
      (resource: ResourceItem) =>
        resource.counter === args.args.counter || // keep if it's the one from args
        !resourceItems.some((datasetItem: ResourceGridRow) => datasetItem.counter === resource.counter),
    );
    return (
      <div className="w-full">
        <FormControl fullWidth sx={wideFormControlSX}>
          <Select
            IconComponent={DownArrowIcon}
            onChange={(e) => handleResourceFields("text", e.target.value)}
            value={resourceEditItemsData.text}
            disabled={editClickedInResource}
            MenuProps={selectMenuProps}
            sx={selectComponentSX}
          >
            {dropdownItems.map((item: ResourceItem) => {
              return (
                <MenuItemComponent key={item.counter} value={item.text}>
                  {item.text}
                </MenuItemComponent>
              );
            })}
          </Select>
        </FormControl>
      </div>
    );
  };
  const resourceAvailabilityEditorTemplate = (args: EditorTemplateArgs<ResourceGridRow>) => {
    return (
      <div className="w-full">
        <TextFieldComponent
          fullWidth
          variant="outlined"
          placeholder="100%"
          defaultValue={args.args.availability}
          onChange={(e) => {
            handleResourceFields("availability", e.target.value);
          }}
          type="number"
        />
      </div>
    );
  };

  const resourceGridColumns = useMemo<ColumnDefinition<ResourceGridRow, keyof ResourceGridRow>[]>(
    () => [
      {
        field: "counter",
        id: "counter",
        width: 40,
        textAlign: "Left",
        sortable: true,
        name: t("ID"),
        editorTemplate: resourceIdEditorTemplate,
      },
      {
        field: "text",
        id: "text",
        width: 50,
        textAlign: "Left",
        sortable: true,
        name: t("Name"),
        editorTemplate: resourceNameEditorTemplate,
      },
      {
        field: "availability",
        id: "availability",
        width: 10,
        textAlign: "Left",
        sortable: true,
        name: t("Availability"),
        editorTemplate: resourceAvailabilityEditorTemplate,
      },
      {
        field: "cost",
        id: "cost",
        width: 10,
        textAlign: "Left",
        sortable: true,
        name: t("Cost"),
      },
    ],

    [editClickedInResource, resourceEditItemsData.counter, resourceEditItemsData.text],
  );

  const handleRowSelection = () => {
    if (slickGridRef.current) {
      const selectedRows = slickGridRef.current.slickGrid.getSelectedRows();
      setDisableEditInResource(selectedRows.length !== 1);
      setDisableDeleteInResource(selectedRows.length < 1);
      setDisableAssignInResource(selectedRows.length > 0);
    }
  };

  const setEmptySelectedRows = () => {
    if (slickGridRef.current) {
      slickGridRef.current.slickGrid.setSelectedRows([]);
      slickGridRef.current.dataView.setSelectedIds([]);
      slickGridRef.current.dataView.selectedRowIds = [];
    }
  };

  const addResourceIds = () => {
    if (!slickGridRef.current) return;
    const resourceItems = slickGridRef.current.dataView.items;
    const newRow = {
      id: uuidv4(),
      resource_id: "",
      counter: "",
      text: "",
      availability: "",
      cost: "",
    };
    const updatedItems = [newRow, ...resourceItems];
    setResourceDataset(updatedItems);
    setResourceEditMode(true);
    setFirstRowSelected(true);
    setDisableEditInResource(false);
  };

  useEffect(() => {
    if (firstRowSelected) {
      setTimeout(() => {
        // KNOWN BUG, preserved verbatim: the grid can unmount before this timer fires and the deref
        // would throw. Erased assertion; adding a guard would change behaviour.
        slickGridRef.current!.slickGrid.setSelectedRows([0]);
      }, 0);
      setFirstRowSelected(false);
    }
  }, [firstRowSelected]);

  const handleDeleteResourceIds = () => {
    if (!slickGridRef.current) return;
    const { dataView, slickGrid } = slickGridRef.current;
    // Selected row indices always address live rows, so the lookup cannot miss. Erased assertion.
    const selectedItems = slickGrid.getSelectedRows().map((row: number) => dataView.items[row]!);
    const selectedItemSet = new Set(selectedItems);

    const selectedId = gantt.getSelectedId();
    const persistedAssignments =
      selectedId && gantt.isTaskExists(selectedId) ? (gantt.getTask(selectedId)["$resourceAssignments"] ?? []) : [];
    const persistedResourceIds = new Set(persistedAssignments.map((resource: ResourceGridRow) => resource.resource_id));
    const deletedResourceIds = new Set(selectedItems.map((item: ResourceGridRow) => item.resource_id));

    setResourceAddEditData(
      resourceAddEditData.filter((resource: ResourceAddEditData) => !deletedResourceIds.has(resource.resource_id)),
    );
    const persistedDeletions = selectedItems.filter((item: ResourceGridRow) =>
      persistedResourceIds.has(item.resource_id),
    );
    if (persistedDeletions.length) {
      // SHAPE GAP: `DeletedResource` requires a branded `resource_id`, but a grid row only carries
      // the loose value it was seeded with. Erased assertion — validating here would change behaviour.
      setDeletedResources([...deletedResources, ...(persistedDeletions as (ResourceGridRow & DeletedResource)[])]);
    }
    setResourceDataset(dataView.items.filter((item: ResourceGridRow) => !selectedItemSet.has(item)));
    setEmptySelectedRows();
  };

  const handleEditResources = () => {
    if (!slickGridRef.current) return;
    const selectedRowdData = slickGridRef.current.dataView.items[slickGridRef.current.slickGrid.getSelectedRows()[0]!]!;
    setResourceEditItemsData(selectedRowdData);
    setEditClickedInResource(true);
    setResourceEditMode(true);
  };
  const handleUpdateResources = () => {
    if (!slickGridRef.current) return;
    if (resourceEditItemsData.counter) {
      const selectedRowIndex = slickGridRef.current.slickGrid.getSelectedRows()?.[0];
      if (selectedRowIndex === undefined) return;

      const existingId = resourceDataset[selectedRowIndex]?.id;
      const updatedResource = {
        ...resourceEditItemsData,
        id: resourceEditItemsData.id || existingId || uuidv4(),
      } as ResourceAddEditData;

      const updatedDataset = [...resourceDataset];
      updatedDataset[selectedRowIndex] = updatedResource;
      setResourceDataset(updatedDataset);

      setResourceAddEditData([...resourceAddEditData, updatedResource]);
      const resourceItems = slickGridRef.current.dataView.getItems();
      const updatedItems = resourceItems.map((resource: ResourceGridRow, index: number) =>
        index === selectedRowIndex ? { ...updatedResource } : { ...resource, id: resource.id || uuidv4() },
      );
      slickGridRef.current.dataView.setItems(updatedItems);
      slickGridRef.current.slickGrid.setSelectedRows([]);
      setResourceEditItemsData({});
      setEditClickedInResource(false);
      setDisableEditInResource(true);
      setResourceEditMode(false);
      setEmptySelectedRows();
    } else {
      dispatch(
        showToastNotification({
          content: t("Please select a Resource"),
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };

  const handleCancelClick = () => {
    setResourceEditMode(false);
    if (!slickGridRef.current) return;
    if (!editClickedInResource) {
      slickGridRef.current.dataView.setItems(
        slickGridRef.current.dataView.items.slice(1, slickGridRef.current.dataView.items.length),
      );
    }
    slickGridRef.current.slickGrid.setSelectedRows([]);
    setDisableEditInResource(true);
    setDisableDeleteInResource(true);
    setDisableAssignInResource(false);
    setResourceEditItemsData({});
    setEditClickedInResource(false);
    setEmptySelectedRows();
  };

  const toolbar: ToolbarItem[] = useMemo(
    () => [
      {
        name: t("Assign"),
        id: "assign",
        prefixIconTemplate: <CreateIcon />,
        cssClass: "font-semibold text-sm",
        buttonType: BUTTON_TYPES.BACKGROUND_BUTTON,
        disabled: disableAssignInResource || resourceEditMode,
      },
      {
        name: t("Edit"),
        id: "edit",
        prefixIconTemplate: <EditIcon />,
        cssClass: "font-semibold text-sm",
        buttonType: BUTTON_TYPES.TEXT_BUTTON,
        disabled: disableEditInResource,
        hidden: resourceEditMode,
      },
      {
        name: t("Update"),
        id: "update",
        prefixIconTemplate: <UpdateIcon />,
        cssClass: "font-semibold text-sm",
        buttonType: BUTTON_TYPES.TEXT_BUTTON,
        hidden: !resourceEditMode,
      },
      {
        name: t("Delete"),
        id: "delete",
        prefixIconTemplate: <DeleteIcon />,
        cssClass: "font-semibold text-sm",
        buttonType: BUTTON_TYPES.TEXT_BUTTON,
        disabled: disableDeleteInResource || resourceEditMode,
        hidden: resourceEditMode,
      },
      {
        name: t("Cancel"),
        id: "cancel",
        prefixIconTemplate: <CancelIcon />,
        cssClass: "font-semibold text-sm",
        buttonType: BUTTON_TYPES.TEXT_BUTTON,
        hidden: !resourceEditMode,
      },
    ],
    [disableAssignInResource, resourceEditMode, disableEditInResource, disableDeleteInResource],
  );
  const toolbarClickHandler = (args: string) => {
    if (args == "assign") {
      addResourceIds();
    } else if (args == "edit") {
      handleEditResources();
    } else if (args == "update") {
      handleUpdateResources();
    } else if (args == "delete") {
      handleDeleteResourceIds();
    } else if (args == "cancel") {
      handleCancelClick();
    }
  };

  return (
    <div className="flex flex-col overflow-auto h-full ps-3 pe-3 pb-3">
      <SlickGrid1
        toolbar={toolbar}
        toolbarClickHandler={toolbarClickHandler}
        ref={slickGridRef}
        columnDefinitions={
          resourceGridColumns as typeof resourceGridColumns & ValidatedColumnDefinition<ResourceGridRow>[]
        }
        gridOptions={slickGridOptions}
        dataset={resourceDataset}
        isEditMode={resourceEditMode}
        onSelectedRowIdsChanged={handleRowSelection}
        hideNoDataIllustration
      />
    </div>
  );
}
export default ResourceInGantt;
